import { callClaudeJSON } from '../utils/claudeProxy';
import { colCharLimits } from '../config/compactCV';

const MODEL = 'claude-opus-5';

const SYSTEM = `Du kortar ned CV-text för svenska byggentreprenad- och konsultanbud
så att den ryms i en tät tabell.

Absoluta regler:
- Du får ALDRIG lägga till fakta som inte finns i indata.
- Du får ALDRIG ändra vilken byggherre eller vilken roll det handlar om.
  Du får förkorta namnet på dem, men det måste avse samma organisation
  respektive samma befattning.
- Du får ALDRIG slå ihop två projekt till ett.
- Du får ALDRIG föreslå att ett projekt stryks bara för att det är gammalt.
  Stryk i första hand det som är minst relevant för anbudet.

Projektnamnet är den enda text som beskriver vad uppdraget var – beskrivningar
visas inte alls i mallen. Behåll därför alltid projekttypen när du kortar:
"Ombyggnad av kvarteret Hästen 21, etapp 2, inkl. installationssamordning"
blir "Kv. Hästen 21, ombyggnad etapp 2" – inte "Kv. Hästen 21".

Rolltitlar normaliseras till vedertagna svenska former: Uppdragsledare,
Projektledare, Bitr. projektledare, Byggledare, Projekteringsledare,
Projektingenjör, Platschef, Entreprenadingenjör, Besiktningsman, BAS-P, BAS-U.

Svara på svenska.`;

const SCHEMA = {
  type: 'object',
  properties: {
    persons: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          userId: { type: 'string' },
          ingress: {
            type: 'string',
            description: 'Nedkortad presentation. Tom sträng om den lämnas orörd.',
          },
          assignments: {
            type: 'array',
            items: {
              type: 'object',
              properties: {
                index: { type: 'integer', description: 'Samma index som i indata.' },
                name: { type: 'string' },
                client: { type: 'string' },
                role: { type: 'string' },
                drop: {
                  type: 'boolean',
                  description: 'true = föreslås strykas om utrymmet inte räcker.',
                },
              },
              required: ['index', 'name', 'client', 'role', 'drop'],
              additionalProperties: false,
            },
          },
        },
        required: ['userId', 'ingress', 'assignments'],
        additionalProperties: false,
      },
    },
    note: {
      type: 'string',
      description: 'En mening om vad som kortades och vad som föreslås strykas.',
    },
  },
  required: ['persons', 'note'],
  additionalProperties: false,
};

/**
 * Kortar ned text så att dokumentet ryms på angivet antal sidor.
 *
 * Måttet på hur mycket som är för mycket (overflowRows) skickas med – utan det
 * kortar modellen godtyckligt och man får sitta och köra om.
 */
export async function shortenForFit({
  persons, tender, overflowRows, maxIngressChars = 300, showBudget = false,
}) {
  const limits = colCharLimits(showBudget);
  const payload = persons.map(p => ({
    userId: p.userId,
    namn: p.name,
    roll_i_anbudet: p.roleInTender || '',
    presentation: p.ingress || '',
    projekt: p.assignments.map(a => ({
      index: a.index,
      ar: a.period,
      namn: a.name,
      byggherre: a.client,
      roll: a.role,
    })),
  }));

  const tenderBlock = tender
    ? `ANBUDET
Namn: ${tender.name || ''}
Kund: ${tender.client || ''}
Beskrivning: ${tender.briefingText || ''}
Efterfrågade roller: ${(tender.requiredRoles || []).join(', ')}
Efterfrågade kompetenser: ${(tender.requiredSkills || []).join(', ')}

`
    : '';

  const content = `${tenderBlock}UTRYMME
Dokumentet är ${overflowRows} rader för långt. En rad = ett projekt.
Korta i första hand text, föreslå strykning (drop) bara för det som behövs
utöver det – och stryk aldrig mer än ${Math.max(overflowRows, 1)} projekt totalt.

TECKENBUDGET PER FÄLT
- projektnamn: max ${limits.name} tecken
- byggherre: max ${limits.client} tecken
- roll: max ${limits.role} tecken
- presentation: max ${maxIngressChars} tecken

Fält som redan ryms lämnas exakt som de är – returnera dem oförändrade.

KONSULTER
${JSON.stringify(payload, null, 1)}`;

  return callClaudeJSON({
    model: MODEL,
    max_tokens: 8000,
    system: SYSTEM,
    schema: SCHEMA,
    messages: [{ role: 'user', content }],
  });
}

/**
 * Jämför AI-svaret mot originalet och returnerar bara faktiska ändringar.
 * Årtalen finns inte i schemat och kan därför inte ha ändrats.
 */
export function diffSuggestion(persons, result) {
  const changes = [];
  const byId = Object.fromEntries(persons.map(p => [p.userId, p]));

  for (const sp of result.persons || []) {
    const person = byId[sp.userId];
    if (!person) continue;

    if (sp.ingress && sp.ingress.trim() && sp.ingress.trim() !== (person.ingress || '').trim()) {
      changes.push({
        userId: sp.userId, personName: person.name, key: 'ingress',
        label: 'Presentation', from: person.ingress || '', to: sp.ingress.trim(),
      });
    }

    for (const sa of sp.assignments || []) {
      const orig = person.assignments.find(a => a.index === sa.index);
      if (!orig) continue;

      for (const field of ['name', 'client', 'role']) {
        const to = (sa[field] || '').trim();
        if (to && to !== (orig[field] || '').trim()) {
          changes.push({
            userId: sp.userId, personName: person.name,
            key: `a${sa.index}.${field}`,
            label: `${orig.period} ${orig.name}`.trim(),
            field,
            from: orig[field] || '', to,
          });
        }
      }

      if (sa.drop) {
        changes.push({
          userId: sp.userId, personName: person.name,
          key: `a${sa.index}.drop`,
          label: `${orig.period} ${orig.name}`.trim(),
          field: 'drop',
          from: 'Med i CV:t', to: 'Föreslås strykas',
        });
      }
    }
  }
  return changes;
}
